import { useState, type FormEvent } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { siteInfo } from "@/mocks/site";

const navItems = [
  { to: "/", label: "首页", end: true },
  { to: "/category/synopsis", label: "剧情介绍", end: false },
  { to: "/category/cast", label: "主演信息", end: false },
  { to: "/category/updates", label: "剧集更新", end: false },
];

export default function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const query = keyword.trim();
    navigate(query ? `/search?q=${encodeURIComponent(query)}` : "/search");
    setMenuOpen(false);
  };

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    [
      "relative whitespace-nowrap px-1 py-2 font-label text-[15px] transition-colors",
      isActive
        ? "text-primary-700 after:absolute after:-bottom-0.5 after:left-0 after:h-0.5 after:w-full after:rounded-full after:bg-accent-500 after:content-['']"
        : "text-foreground-600 hover:text-primary-700",
    ].join(" ");

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex h-16 items-center gap-4 md:h-[72px]">
          <Link to="/" className="flex items-center gap-2.5" onClick={() => setMenuOpen(false)}>
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-700 text-background-50">
              <i className="ri-movie-2-line text-lg leading-none"></i>
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-[17px] font-bold tracking-wide text-primary-900">
                {siteInfo.brand}
              </span>
              <span className="mt-0.5 font-label text-[10px] uppercase tracking-[0.2em] text-foreground-400">
                {siteInfo.titleEn}
              </span>
            </span>
          </Link>

          <nav className="ml-4 hidden items-center gap-7 lg:flex">
            {navItems.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end} className={linkClass}>
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-2 md:gap-3">
            <form
              onSubmit={handleSearch}
              className="hidden items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3 py-1.5 md:flex"
            >
              <span className="flex h-4 w-4 items-center justify-center text-foreground-400">
                <i className="ri-search-line text-base leading-none"></i>
              </span>
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                type="search"
                placeholder="搜索剧情 / 演员"
                className="w-28 bg-transparent font-label text-sm text-foreground-800 outline-none placeholder:text-foreground-400 lg:w-36"
                aria-label="站内搜索"
              />
            </form>

            <Link
              to="/search"
              className="hidden whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 font-label text-sm font-medium text-background-50 transition-colors hover:bg-accent-600 md:inline-flex"
            >
              立即搜索
            </Link>

            <button
              type="button"
              aria-label="打开菜单"
              onClick={() => setMenuOpen((open) => !open)}
              className="flex h-10 w-10 items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-700 lg:hidden"
            >
              <i className={menuOpen ? "ri-close-line text-xl leading-none" : "ri-menu-line text-xl leading-none"}></i>
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 lg:hidden">
          <div className="w-full px-4 py-4 md:px-8">
            <form
              onSubmit={handleSearch}
              className="mb-4 flex items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3 py-2"
            >
              <span className="flex h-4 w-4 items-center justify-center text-foreground-400">
                <i className="ri-search-line text-base leading-none"></i>
              </span>
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                type="search"
                placeholder="搜索剧情 / 演员"
                className="w-full bg-transparent font-label text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
                aria-label="站内搜索"
              />
              <button type="submit" className="whitespace-nowrap font-label text-sm text-primary-700">
                搜索
              </button>
            </form>
            <nav className="flex flex-col">
              {[...navItems, { to: "/search", label: "搜索", end: false }].map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    [
                      "rounded-md px-3 py-3 font-label text-[15px] transition-colors",
                      isActive
                        ? "bg-primary-50 text-primary-700"
                        : "text-foreground-700 hover:bg-background-100",
                    ].join(" ")
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
          </div>
        </div>
      )}
    </header>
  );
}